import { Component, forwardRef, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { DurationPickerComponent } from "../../../../_shared/components/duration-picker/duration-picker.component";
import { FormsModule } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-duration-picker-input',
  standalone: true,
  imports: [NgbDropdownModule, DurationPickerComponent, FormsModule],
  templateUrl: './duration-picker-input.component.html',
  styleUrls: ['./duration-picker-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DurationPickerInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DurationPickerInputComponent),
      multi: true
    }
  ]
})
export class DurationPickerInputComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() required: boolean = false;

  formattedDuration = '';
  totalSeconds: number = 0;
  disabled: boolean = false;
  errorMessage: string | null = null;
  toggle: boolean = false;

  private transloco = inject(TranslocoService);

  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  ngOnInit() { }

  onValueChange(seconds: number): void {
    this.totalSeconds = seconds;
    this.formattedDuration = this.formatDuration(seconds);
    this.onChange(this.totalSeconds); // Notify Angular forms of the value change
    this.onTouched();
  }

  formatDuration(totalSeconds: number): string {
    let hours = Math.floor(totalSeconds / 3600);
    let minutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = totalSeconds % 60;

    let timeParts: string[] = [];

    if (hours > 0) {
      timeParts.push(this.transloco.translate(hours !== 1 ? 'driverManager.duration.hours' : 'driverManager.duration.hour', { count: hours }));
    }

    if (minutes > 0) {
      timeParts.push(this.transloco.translate(minutes !== 1 ? 'driverManager.duration.minutes' : 'driverManager.duration.minute', { count: minutes }));
    }

    if (seconds > 0) {
      timeParts.push(this.transloco.translate(seconds !== 1 ? 'driverManager.duration.seconds' : 'driverManager.duration.second', { count: seconds }));
    }

    return timeParts.join(' ').trim();
  }

  onDropdownToggle(toggle: boolean): void {
    this.toggle = toggle;

    if (!this.toggle) {
      this.onChange(this.totalSeconds);
    }
  }

  // ControlValueAccessor methods

  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      this.totalSeconds = value;
      this.formattedDuration = this.formatDuration(value);
    } else {
      this.totalSeconds = 0;
      this.formattedDuration = '';
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.totalSeconds <= 0 && !this.toggle) {
      this.errorMessage = this.transloco.translate('driverManager.duration.minimumValidation');
      return { invalidDuration: true };
    }

    this.errorMessage = null;
    return null;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}